import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Link,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { SemAlcool, SemGluten, LowCarb } from 'assets/icons';
import SEOHead from 'components/SEOHead';
import { useGrowlerCart } from 'services/cart/GrowlerCartContext';
import { GrowlerService } from 'services/firebase/growlerService';
import {
  GROWLER_PACKAGE_LABELS,
  getGrowlerPackageType,
} from 'utils/growlersFlow';
import type { Growler } from 'utils/types/Growler';
import styles from 'AppRoutes/BeerDetail/styles';

const GrowlerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart, openCart } = useGrowlerCart();
  const [growler, setGrowler] = useState<Growler | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const loadGrowler = async () => {
      if (!id) {
        setError('Produto não encontrado');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await GrowlerService.getGrowlerById(id);
        if (!data) {
          setError('Produto não encontrado');
        } else {
          setGrowler(data);
        }
      } catch (err) {
        console.error(err);
        setError('Erro ao carregar detalhes do produto');
      } finally {
        setLoading(false);
      }
    };

    loadGrowler();
  }, [id]);

  const handleAddToCart = () => {
    if (growler?.inStock) {
      addToCart(growler, quantity);
      openCart();
    }
  };

  if (loading) {
    return (
      <Container sx={styles.container}>
        <Box sx={styles.loadingContainer}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Carregando produto...</Typography>
        </Box>
      </Container>
    );
  }

  if (error || !growler) {
    return (
      <Container sx={styles.container}>
        <Alert severity="error" sx={styles.errorAlert}>
          {error || 'Produto não encontrado'}
        </Alert>
        <Button
          variant="outlined"
          onClick={() => navigate('/growlers')}
          sx={{ mt: 2 }}
        >
          Voltar ao catálogo
        </Button>
      </Container>
    );
  }

  const originalPrice = growler.originalPrice;
  const hasPriceDiscount =
    typeof originalPrice === 'number' && originalPrice > growler.price;
  const discountPercentage = hasPriceDiscount
    ? Math.round(((originalPrice - growler.price) / originalPrice) * 100)
    : 0;

  return (
    <Container sx={styles.container}>
      <SEOHead
        title={`${growler.name} | Growlers e Garrafas - Clube da 42`}
        description={growler.description}
      />
      <Breadcrumbs separator="›" sx={styles.breadcrumbs}>
        <Link
          underline="hover"
          sx={styles.breadcrumbLink}
          onClick={() => navigate('/')}
        >
          Home
        </Link>
        <Link
          underline="hover"
          sx={styles.breadcrumbLink}
          onClick={() => navigate('/growlers')}
        >
          Growlers
        </Link>
        <Typography sx={styles.breadcrumbCurrent}>{growler.name}</Typography>
      </Breadcrumbs>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Box sx={styles.imageWrapper}>
            {(growler.semAlcool || growler.semGluten) && (
              <Box sx={styles.stampsBadge}>
                {growler.semAlcool && (
                  <Box
                    component="img"
                    src={SemAlcool}
                    alt="Sem álcool"
                    sx={styles.stampIcon}
                  />
                )}
                {growler.semGluten && (
                  <Box
                    component="img"
                    src={SemGluten}
                    alt="Sem glúten"
                    sx={styles.stampIcon}
                  />
                )}
              </Box>
            )}
            {growler.lowCarb && (
              <Box sx={styles.stampsBadgeRight}>
                <Box
                  component="img"
                  src={LowCarb}
                  alt="Low Carb"
                  sx={styles.stampIcon}
                />
              </Box>
            )}
            <Box sx={{ ...styles.imageContainer, backgroundColor: '#fff' }}>
              <img
                src={growler.image}
                alt={growler.name}
                style={styles.productImage}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ ...styles.priceCard, bgcolor: '#fff' }}>
            <CardContent sx={styles.cardContent}>
              <Typography variant="h4" sx={styles.productName}>
                {growler.name}
              </Typography>
              <Chip
                size="small"
                label={GROWLER_PACKAGE_LABELS[getGrowlerPackageType(growler)]}
                sx={{ mt: 1, mb: 1, fontWeight: 600 }}
              />
              <Typography variant="h6" sx={styles.brewery}>
                {growler.brewery}
              </Typography>
              <Typography variant="body1" sx={styles.productInfo}>
                {growler.style} • {growler.abv}% ABV • {growler.volume}ml
              </Typography>

              <Box sx={styles.priceSection}>
                {hasPriceDiscount && (
                  <Box sx={styles.discountBadge}>
                    <LocalOfferIcon sx={styles.discountIcon} />
                    <Typography variant="caption" sx={styles.discountText}>
                      -{discountPercentage}%
                    </Typography>
                  </Box>
                )}
                {hasPriceDiscount && (
                  <Typography variant="h6" sx={styles.originalPrice}>
                    R$ {originalPrice.toFixed(2)}
                  </Typography>
                )}
                <Typography variant="h4" sx={styles.currentPrice}>
                  R$ {growler.price.toFixed(2)}
                </Typography>
              </Box>

              {/* Futuro: preço para assinante
              <Box sx={styles.subscriberSection}>
                <Typography>PREÇO ASSINANTE</Typography>
                <Typography>R$ {growler.subscriberPrice?.toFixed(2)}</Typography>
              </Box>
              */}

              <Box sx={styles.quantitySection}>
                <Typography sx={styles.quantityLabel}>Quantidade:</Typography>
                <Box sx={styles.quantityControls}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                    disabled={quantity <= 1}
                    sx={styles.quantityButton}
                  >
                    -
                  </Button>
                  <Typography variant="h6" sx={styles.quantityValue}>
                    {quantity}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setQuantity(prev => prev + 1)}
                    sx={styles.quantityButton}
                  >
                    +
                  </Button>
                </Box>
              </Box>

              <Button
                variant="contained"
                size="large"
                fullWidth
                disabled={!growler.inStock}
                startIcon={<AddShoppingCartIcon />}
                onClick={handleAddToCart}
                sx={styles.addToCartButton}
              >
                {growler.inStock ? 'ADICIONAR AO CARRINHO' : 'INDISPONÍVEL'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ ...styles.descriptionSection, backgroundColor: '#fff' }}>
        <Typography variant="h5" sx={styles.sectionTitle}>
          SAIBA MAIS SOBRE ESTE PRODUTO
        </Typography>
        <Typography variant="body1" sx={styles.description}>
          {growler.description}
        </Typography>
        <Grid container spacing={2} sx={styles.technicalInfo}>
          <Grid item xs={6} sm={3}>
            <Typography sx={styles.infoLabel}>Teor Alcoólico</Typography>
            <Typography sx={styles.infoValue}>{growler.abv}% ABV</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography sx={styles.infoLabel}>Volume</Typography>
            <Typography sx={styles.infoValue}>{growler.volume}ml</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography sx={styles.infoLabel}>Estilo</Typography>
            <Typography sx={styles.infoValue}>{growler.style}</Typography>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default GrowlerDetail;
